import React, {FC, useLayoutEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {IconButton} from '../../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {TooltipCoordinator} from '../../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {buildTeammateDisplayName} from '../../../../../../../helpers/modelHelpers';
import {ApplicationModel} from '../../../../../../../models/api/applicationModel';
import {TeammateModel} from '../../../../../../../models/api/teammateModel';
import {TopicModel} from '../../../../../../../models/api/topicModel';
import {TeammateAvatar} from '../../../../../../common/avatars/teammateAvatar';
import {useLinkNavigation} from '../../../../../../common/navigation/useLinkNavigation';
import {CommentViewerReferencePreviewerUnfurlIcon} from './commentViewerReferencePreviewerUnfurlIcon';

/*
 * Props.
 */

interface CommentViewerReferencePreviewerUnfurlProps {
  className?: string;
  topic?: TopicModel;
  owner?: TeammateModel;
  application?: ApplicationModel;
  isInteractionDisabled?: boolean;
  shouldDefaultExpanded?: boolean;
}

/*
 * Style.
 */

const StyledPreviewerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  padding: 12px;
  margin: 4px 12px 12px 12px;
  width: 318px;
  background-color: ${(p) => p.theme.greys.shade10};
  border-radius: 6px;
`;

const StyledIconWrapperDiv = styled.div`
  height: 16px;
  display: flex;
  align-items: center;
`;

const StyledContentWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  gap: 4px;
`;

const StyledHeaderRowDiv = styled.div`
  display: grid;
  grid-template-areas: 'title actions';
  grid-template-columns: 1fr auto;
  gap: 4px;
  height: 16px;
  align-items: center;
`;

const StyledHeaderLinkDiv = styled.div`
  color: ${(p) => p.theme.palette.blue.shade40};
  font-size: ${fontSizes.verySmall};
  overflow: hidden;
  font-weight: ${fontWeights.semibold};

  &:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }
`;

const StyledDescriptionWrapperDiv = styled.div`
  overflow: hidden;
`;

interface StyledDescriptionDivProps {
  $shouldShowOverflow: boolean;
}

const StyledDescriptionDiv = styled.div<StyledDescriptionDivProps>`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-word;
  text-overflow: ellipsis;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  line-height: 16px;
  user-select: text;

  ${(p) =>
    p.$shouldShowOverflow &&
    css`
      -webkit-line-clamp: unset;
    `};
`;

const StyledIconButton = styled(IconButton)`
  margin: -6px -6px -6px 0;
`;

const StyledOwnerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledOwnerNameDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 16px;
`;

/*
 * Component.
 */

export const CommentViewerReferencePreviewerUnfurl: FC<CommentViewerReferencePreviewerUnfurlProps> = (
  props,
) => {
  const {application, topic, owner, className, shouldDefaultExpanded = false} = props;
  const {navigateToLink} = useLinkNavigation();
  const [shouldShowOverflow, setShouldShowOverflow] = useState(false);

  const [overflowRef, setOverflowRef] = useState<HTMLDivElement>();
  const [isOverflowing, setIsOverflowing] = useState(false);
  // Track if we opened the overflow by default.
  const setDefaultRef = useRef(false);

  useLayoutEffect(() => {
    if (!overflowRef) {
      return setIsOverflowing(false);
    }
    const isOverflowRefOverflowing = overflowRef.scrollHeight > overflowRef.offsetHeight;
    if (isOverflowRefOverflowing && shouldDefaultExpanded && !setDefaultRef.current) {
      setDefaultRef.current = true;
      setShouldShowOverflow(true);
    }
    return setIsOverflowing(isOverflowRefOverflowing);
  }, [overflowRef, shouldDefaultExpanded]);

  if (!topic) {
    return null;
  }
  return (
    <StyledPreviewerWrapperDiv className={className}>
      <StyledIconWrapperDiv>
        <CommentViewerReferencePreviewerUnfurlIcon application={application} />
      </StyledIconWrapperDiv>
      <StyledContentWrapperDiv>
        <StyledHeaderRowDiv>
          <StyledHeaderLinkDiv onClick={() => navigateToLink(topic.externalLink)}>
            <TooltipCoordinator render={() => topic.name} placement="top">
              <TooltipOverflowCondition>{topic.name}</TooltipOverflowCondition>
            </TooltipCoordinator>
          </StyledHeaderLinkDiv>
          {(isOverflowing || shouldShowOverflow) && (
            <StyledIconButton
              iconName={shouldShowOverflow ? 'chevronUpSmall' : 'chevronDownSmall'}
              label={null}
              onClick={() => setShouldShowOverflow((val) => !val)}
            />
          )}
        </StyledHeaderRowDiv>
        {owner && (
          <StyledOwnerWrapperDiv>
            <TeammateAvatar teammate={owner} size={16} />
            <StyledOwnerNameDiv>{buildTeammateDisplayName(owner)}</StyledOwnerNameDiv>
          </StyledOwnerWrapperDiv>
        )}
        {topic.description && (
          <StyledDescriptionWrapperDiv
            ref={(ref) => {
              if (ref) {
                setOverflowRef(ref);
              }
            }}
          >
            <StyledDescriptionDiv $shouldShowOverflow={shouldShowOverflow}>
              {topic.description}
            </StyledDescriptionDiv>
          </StyledDescriptionWrapperDiv>
        )}
      </StyledContentWrapperDiv>
    </StyledPreviewerWrapperDiv>
  );
};
